import { memo, useEffect, useId, useReducer, useRef, useState } from 'react'
import {
    Crown, Expand, Maximize2, Minimize2, MonitorPlay, PictureInPicture2,
    RotateCcw, Shrink, Video, Volume2, VolumeX, WifiOff, ZoomIn, ZoomOut, X
} from 'lucide-react'
import type { ShareKind } from '@shared/protocol'
import { MriSpinner, MriStatusBadge, cn } from '@mriqbox/ui-kit'
import { Avatar } from '@/components/Avatar'
import { useTileZoom } from '@/hooks/useTileZoom'
import { useFullscreen } from '@/hooks/useFullscreen'
import { TileMenu, type TileMenuAction } from '@/components/TileMenu'
import { ZoomMinimap } from '@/components/ZoomMinimap'
import type { PeerConnectionState } from '@/lib/transport'
import { qualityLabel, renderedFrames, type StreamMeasure } from '@/lib/streamQuality'
import { displayedShortSide, receiveLimit, type ReceiveLimit, type ReceivePreference } from '@/lib/receiveQuality'

/** De quanto em quanto tempo o fps do `<video>` e relido. */
const MEASURE_INTERVAL_MS = 2_000

export interface StageStream {
    /** `peerId:kind` — quem transmite tela e camera aparece em dois tiles. */
    id: string
    /** Id da pessoa. O `id` do tile nao serve: ele carrega o tipo junto. */
    peerId: string
    nickname: string
    avatar?: string
    isSelf: boolean
    isHost: boolean
    /** My own tile: how many people are watching it. */
    watchers?: number
    /** Tela ou camera — muda o icone e se a imagem aparece espelhada. */
    kind: ShareKind
    /** `null` enquanto a conexao P2P ainda esta sendo montada. */
    stream: MediaStream | null
}

export interface StreamTileProps {
    entry: StageStream
    /** Ocupando o palco inteiro (modo destaque). */
    focused?: boolean
    /** Miniatura da tira lateral — esconde os controles. */
    compact?: boolean
    style?: React.CSSProperties
    className?: string
    onToggleFocus?: (id: string) => void
    /** Manda SO esta transmissao pra janela flutuante. */
    onPip?: (id: string) => void
    /**
     * Tira esta transmissao do palco — pra QUEM ASSISTE, so nesta aba.
     *
     * Nao e um "esconder" visual: o tile sai do DOM, e com ele o `<video>`
     * que estava sendo decodificado e desenhado a cada quadro. Com muita gente
     * compartilhando, e o que devolve a GPU pra quem so quer ver uma tela.
     */
    onHide?: (id: string) => void
    /** Esta e a que esta na janela flutuante agora. */
    isPip?: boolean
    /**
     * Som ligado e volume desta transmissao.
     *
     * Vem de fora de proposito: o tile e remontado sempre que o id do
     * participante muda (uma reconexao em que a retomada nao pegou), e estado
     * local voltaria ao padrao — a transmissao "mutava sozinha" pra quem
     * assistia, junto com o volume que a pessoa tinha ajustado.
     */
    audioOn?: boolean
    volume?: number
    onAudioChange?: (id: string, audioOn: boolean) => void
    onVolumeChange?: (id: string, volume: number) => void
    /**
     * Avisa o tamanho real do que esta chegando.
     *
     * Serve pra duas coisas: o mosaico usa a proporcao pra nao deixar tarja
     * preta, e a etiqueta de qualidade usa a altura. Sao a mesma medida, e o
     * `<video>` e o unico lugar onde ela existe de graca — por isso sobe daqui
     * em vez de cada interessado medir por conta.
     */
    onMeasure?: (id: string, measure: StreamMeasure) => void
    /**
     * Bits por segundo medidos pelo transporte. `undefined` quando nao ha
     * conexao de midia pra medir — sozinho numa sala em modo mesh, por
     * exemplo, onde nada esta sendo codificado nem enviado.
     */
    bitrate?: number
    /** O servidor liga a medicao de cadencia e banda (`STREAM_STATS`). */
    statsEnabled?: boolean
    /** Fone silenciado: cala tambem o som das transmissoes, nao so a voz. */
    deafened?: boolean
    /** Quem transmite esta falando no microfone agora. */
    speaking?: boolean
    /**
     * Conexao de midia com esse peer, vinda do `useRoomSession` (mesh e SFU
     * publicam no mesmo vocabulario — a UI nao precisa saber qual dos dois
     * esta no ar). Sobe do `RoomPage` pelo `Stage` como qualquer outra prop;
     * `undefined` quando quem monta o tile nao tem essa informacao.
     */
    peerState?: PeerConnectionState
    /** What this viewer wants to receive; the tile turns it into a cap for its size. */
    receivePreference?: ReceivePreference
    /** `limit === undefined` withdraws this tile's request. */
    onReceiveLimit?: (
        requester: string,
        peerId: string,
        streamId: string,
        limit: ReceiveLimit | null | undefined
    ) => void
}

/** Waits for resizing and wheel zoom to settle before asking the sender again. */
const RECEIVE_LIMIT_DEBOUNCE_MS = 400

function StreamTileBase({
    entry,
    focused = false,
    compact = false,
    style,
    className,
    onToggleFocus,
    onPip,
    onHide,
    isPip = false,
    audioOn = false,
    volume = 1,
    onAudioChange,
    onVolumeChange,
    onMeasure,
    bitrate,
    statsEnabled = false,
    deafened = false,
    speaking = false,
    peerState,
    receivePreference = 'auto',
    onReceiveLimit
}: StreamTileProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    /** Force um render novo: quem manda no `hasAudio` sao as trilhas da stream. */
    const [, refresh] = useReducer((tick: number) => tick + 1, 0)
    // Comeca mudo: navegador bloqueia autoplay com audio, e a propria tela
    // voltando pra voce causaria eco. Quem lembra da escolha e a pagina.
    const muted = !audioOn
    const setMuted = (proximo: boolean | ((atual: boolean) => boolean)) => {
        const valor = typeof proximo === 'function' ? proximo(muted) : proximo
        onAudioChange?.(entry.id, !valor)
    }
    /**
     * Tem som pra ouvir?
     *
     * Derivado no render, e nao guardado em estado num efeito de entrada: a
     * MediaStream e a mesma o tempo todo e vai GANHANDO trilhas — o video da
     * tela chega primeiro e o audio dela logo depois. Decidir isso uma vez so
     * congelava a resposta em "sem audio", e o botao de som nunca aparecia.
     *
     * Nunca no proprio tile: ninguem precisa se ouvir, e o navegador devolveria
     * o som que acabou de sair da maquina.
     */
    const hasAudio = !entry.isSelf && (entry.stream?.getAudioTracks().length ?? 0) > 0
    /**
     * A MINHA tela subiu sem trilha de audio.
     *
     * So no proprio tile, e so em tela: a camera e capturada sem audio de
     * proposito, e quem assiste nao pode fazer nada a respeito — o que falta
     * e uma caixinha no seletor de quem transmite. O aviso de texto some
     * depois de alguns segundos; esta marca fica enquanto a transmissao
     * estiver no ar, que e o tempo em que a pergunta "cade o som?" aparece.
     */
    const semSom = entry.isSelf
        && entry.kind === 'screen'
        && !!entry.stream
        && entry.stream.getAudioTracks().length === 0
    /**
     * Trilha de video pausada pela rede (WebRTC nao envia frames). Diferente de
     * `ended`: e transitorio e costuma resolver sozinho. Enquanto dura, mostra um
     * indicador visual pra pessoa nao achar que tudo travou.
     */
    const [trackMuted, setTrackMuted] = useState(false)
    /**
     * Tamanho do que esta chegando AGORA.
     *
     * Nao e o que quem transmite escolheu no menu: com simulcast os dois
     * divergem de proposito, e e este numero — o que de fato chegou — que
     * explica pra pessoa por que a imagem piorou.
     */
    const [size, setSize] = useState({ width: 0, height: 0 })
    /**
     * Quadros por segundo que este `<video>` esta DESENHANDO.
     *
     * Medido aqui, e nao em `getStats()`, por dois motivos. O primeiro e que
     * existe sozinho numa sala: em modo mesh, sem ninguem do outro lado nao ha
     * conexao nenhuma pra `getStats()` ler, e quem esta so conferindo a
     * propria tela ficaria sem numero. O segundo e que quadro desenhado e o
     * que a pessoa de fato ve.
     */
    const [fps, setFps] = useState<number | null>(null)

    /**
     * Estado da conexao com quem transmite.
     *
     * Nao ha um terceiro vocabulario aqui: `connecting` reforca o que o tile ja
     * dizia ("conectando" quando nao ha stream, "conexao fraca" quando a imagem
     * congela) e `failed` e o unico estado novo — o momento em que o transporte
     * desistiu e a pessoa precisa saber que aquele video nao vem mais.
     */
    // Nunca no proprio tile: nao existe "conexao comigo mesmo".
    const connection = entry.isSelf ? undefined : peerState
    const connectionFailed = connection === 'failed'
    /**
     * A PUBLICACAO caiu — nao a conexao com outra pessoa, e a sua saida pro
     * SFU. So `failed` importa aqui: a publicacao passa por `connecting`
     * toda vez que a pessoa comeca a compartilhar, e alarmar nesse trecho
     * normal seria falso positivo constante.
     */
    const selfPublishFailed = entry.isSelf && peerState === 'failed'
    /**
     * A conexao com esse peer ja esteve de pe alguma vez?
     *
     * `ontrack` dispara no `setRemoteDescription`, ANTES de o ICE fechar: sem
     * essa memoria, toda entrada normal piscaria a tarja de conexao fraca no
     * meio de um handshake perfeitamente saudavel.
     */
    const [everConnected, setEverConnected] = useState(false)
    useEffect(() => setEverConnected(false), [entry.peerId])
    useEffect(() => {
        if (connection === 'connected') setEverConnected(true)
    }, [connection])
    /** Conexao em recuperacao com a imagem ja no ar: a mesma tarja de sempre. */
    const recovering = everConnected && connection === 'connecting' && !!entry.stream

    const {
        containerRef,
        scale,
        pan,
        isDragging,
        isZoomed,
        viewportBox,
        zoomIn,
        zoomOut,
        setScaleDirect,
        panToNormalized,
        resetZoom,
        onWheel,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        hasDragged
    } = useTileZoom({ disabled: compact })

    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        video.srcObject = entry.stream
        setTrackMuted(false)

        const tryPlay = () => {
            if (video.paused && video.srcObject) void video.play().catch(() => undefined)
        }
        if (entry.stream) tryPlay()

        // Monitora mute/unmute da trilha de video: quando a rede oscila, o
        // WebRTC para de enviar frames e a trilha fica `muted`. Sem isso, o
        // video congela no ultimo frame e a pessoa acha que travou tudo.
        const videoTrack = entry.stream?.getVideoTracks()[0]
        const onMute = () => setTrackMuted(true)
        const onUnmute = () => {
            setTrackMuted(false)
            // Alguns navegadores pausam o <video> quando a trilha fica muted;
            // rechamar play() garante que ele retoma assim que os frames voltam.
            tryPlay()
        }
        videoTrack?.addEventListener('mute', onMute)
        videoTrack?.addEventListener('unmute', onUnmute)

        /**
         * `resize` no `<video>` e o aviso de que a resolucao QUE CHEGA mudou.
         *
         * E o evento que o simulcast dispara ao trocar de camada, e ele custa
         * zero: o navegador ja sabe disso pra desenhar o quadro. Medir a mesma
         * coisa por `getStats()` seria pagar por um dado que esta aqui.
         */
        const medir = () => {
            const { videoWidth, videoHeight } = video
            if (videoWidth <= 0 || videoHeight <= 0) return
            setSize((atual) => (atual.width === videoWidth && atual.height === videoHeight
                ? atual
                : { width: videoWidth, height: videoHeight }))
        }
        medir()
        video.addEventListener('loadedmetadata', medir)
        video.addEventListener('resize', medir)

        // Aba que volta ao foco: o navegador pode ter pausado o <video> enquanto
        // estava em background. O Jitsi faz a mesma coisa.
        const onVisibility = () => {
            if (document.visibilityState === 'visible') tryPlay()
        }
        document.addEventListener('visibilitychange', onVisibility)

        return () => {
            videoTrack?.removeEventListener('mute', onMute)
            videoTrack?.removeEventListener('unmute', onUnmute)
            video.removeEventListener('loadedmetadata', medir)
            video.removeEventListener('resize', medir)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [entry.stream, entry.isSelf])

    useEffect(() => {
        const video = videoRef.current
        if (!video || !statsEnabled || !entry.stream) {
            setFps(null)
            return
        }

        // Contador acumulado: o fps sai da diferenca entre duas leituras.
        let previous = renderedFrames(video)
        let previousAt = performance.now()
        if (previous === null) return

        const timer = window.setInterval(() => {
            const current = renderedFrames(video)
            const now = performance.now()
            const seconds = (now - previousAt) / 1000
            // Trilha trocada zera o contador; sem a guarda, viraria negativo.
            if (current !== null && previous !== null && current >= previous && seconds > 0) {
                setFps(Math.round((current - previous) / seconds))
            }
            previous = current
            previousAt = now
        }, MEASURE_INTERVAL_MS)

        return () => window.clearInterval(timer)
    }, [entry.stream, statsEnabled])

    // Sobe a medida pra quem precisa dela fora do tile: o palco, pra montar o
    // mosaico sem tarja preta, e a lista de participantes, pra etiqueta.
    useEffect(() => {
        if (size.width > 0 && size.height > 0) {
            onMeasure?.(entry.id, { width: size.width, height: size.height, fps })
        }
    }, [entry.id, size, fps, onMeasure])

    const [tileBox, setTileBox] = useState({ width: 0, height: 0 })
    useEffect(() => {
        const container = containerRef.current
        if (!container || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(([item]) => {
            if (!item) return
            const width = Math.round(item.contentRect.width)
            const height = Math.round(item.contentRect.height)
            setTileBox((current) => (current.width === width && current.height === height ? current : { width, height }))
        })
        observer.observe(container)
        return () => observer.disconnect()
    }, [containerRef])

    const remoteStream = !entry.isSelf ? entry.stream : null
    const shortSide = displayedShortSide(tileBox, size, window.devicePixelRatio || 1, scale)
    const limit = remoteStream && shortSide > 0 ? receiveLimit(receivePreference, shortSide) : null
    const limitKey = limit ? `${limit.maxShortSide}@${limit.maxFps}` : 'none'
    const latestLimit = useRef(limit)
    latestLimit.current = limit
    const requester = useId()
    const laidOut = shortSide > 0
    useEffect(() => {
        // Before layout the tile has no size, and asking for the source there would undo a cap.
        if (!remoteStream || !onReceiveLimit || !laidOut) return
        const timer = window.setTimeout(() => {
            onReceiveLimit(requester, entry.peerId, remoteStream.id, latestLimit.current)
        }, RECEIVE_LIMIT_DEBOUNCE_MS)
        return () => window.clearTimeout(timer)
    }, [limitKey, remoteStream, entry.peerId, onReceiveLimit, laidOut, requester])
    useEffect(() => {
        if (!remoteStream || !onReceiveLimit) return
        return () => onReceiveLimit(requester, entry.peerId, remoteStream.id, undefined)
    }, [remoteStream, entry.peerId, onReceiveLimit, requester])

    useEffect(() => {
        if (videoRef.current) videoRef.current.volume = Math.max(0, Math.min(1, volume))
    }, [volume])

    /**
     * A trilha de audio costuma chegar depois do video, na mesma MediaStream.
     * O `addtrack` e o aviso do navegador de que isso aconteceu — sem ele, o
     * tile so notaria o som na proxima vez que o palco re-renderizasse por
     * outro motivo.
     */
    useEffect(() => {
        const stream = entry.stream
        if (!stream || typeof stream.addEventListener !== 'function') return

        stream.addEventListener('addtrack', refresh)
        stream.addEventListener('removetrack', refresh)
        return () => {
            stream.removeEventListener('addtrack', refresh)
            stream.removeEventListener('removetrack', refresh)
        }
    }, [entry.stream, refresh])

    useEffect(() => {
        if (!isZoomed) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') resetZoom()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [isZoomed, resetZoom])

    // A propria camera vem espelhada, como num espelho de verdade — e o que a
    // pessoa espera ao se ver. Pros outros vai normal, senao texto na camisa
    // (ou atras) apareceria invertido.
    const mirrored = entry.isSelf && entry.kind === 'camera'

    /**
     * `1080p60 · 4.2 Mbps` — ou so `1080p` quando a medicao esta desligada.
     *
     * Vazia enquanto o `<video>` nao souber o tamanho: um "· 4.2 Mbps" solto
     * num tile que ainda esta conectando informa menos do que nada.
     */
    /**
     * Tela cheia DESTA transmissao.
     *
     * O botao da barra de cima leva a sala inteira — barra, lista lateral e
     * palco —, entao o video continuava espremido no mesmo pedaco, so que numa
     * janela maior. Aqui o alvo e o proprio tile: o video ocupa a tela, sem
     * mais nada em volta, que e o que se espera de um botao de tela cheia.
     */
    const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(containerRef)
    /** Onde o menu de contexto foi aberto; `null` = fechado. */
    const [menuEm, setMenuEm] = useState<{ x: number; y: number } | null>(null)

    const acoesDoMenu: TileMenuAction[] = [
        {
            id: 'fullscreen',
            label: isFullscreen ? 'Sair da tela cheia' : 'Ver em tela cheia',
            icon: 'fullscreen',
            onSelect: () => void toggleFullscreen()
        },
        ...(hasAudio ? [{
            id: 'audio',
            label: muted ? 'Ouvir o som' : 'Silenciar',
            icon: (muted ? 'audio-off' : 'audio-on') as TileMenuAction['icon'],
            active: !muted,
            onSelect: () => setMuted(!muted)
        }] : []),
        ...(onPip ? [{
            id: 'pip',
            label: isPip ? 'Trazer da janela flutuante' : 'Ver em janela flutuante',
            icon: 'pip' as TileMenuAction['icon'],
            active: isPip,
            onSelect: () => onPip(entry.id)
        }] : []),
        ...(onHide ? [{
            id: 'hide',
            label: entry.isSelf ? 'Ocultar minha prévia' : 'Parar de assistir',
            icon: 'hide' as TileMenuAction['icon'],
            onSelect: () => onHide(entry.id)
        }] : [])
    ]

    const etiqueta = qualityLabel({
        width: size.width,
        height: size.height,
        fps,
        bitrate: bitrate ?? null
    })

    return (
        <div
            ref={containerRef}
            /**
             * Em tela cheia o tamanho vem do mosaico em PIXELS (o `style` que
             * o Stage passa). Mantido, ele venceria a tela cheia e o video
             * apareceria do tamanho da miniatura no meio de uma tela preta.
             */
            style={isFullscreen ? undefined : style}
            className={cn(
                'group relative overflow-hidden rounded-xl border border-border/70 bg-black shadow-lg shadow-black/40 select-none',
                'transition-[border-color,box-shadow] duration-200',
                focused ? 'border-primary/50' : 'hover:border-primary/40',
                // Quem esta falando ganha o mesmo anel verde da barra lateral:
                // no palco cheio, e o que diz de qual tela vem a voz.
                speaking && 'border-emerald-500 shadow-emerald-500/20',
                isZoomed && (isDragging ? 'cursor-grabbing touch-none' : 'cursor-grab touch-none'),
                className
            )}
            data-testid={`tile-${entry.id}`}
            onWheel={onWheel}
            onContextMenu={(event) => {
                // Sem o menu do navegador por cima: o daqui e sobre a
                // transmissao, nao sobre a pagina.
                event.preventDefault()
                setMenuEm({ x: event.clientX, y: event.clientY })
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            /**
             * Duplo clique = tela cheia, como em qualquer player de video.
             *
             * Antes ele alternava o destaque, que e o mesmo que o botao de
             * expandir ja faz — o gesto ficava gasto numa acao que tinha atalho
             * proprio, e a que a pessoa espera dele nao existia.
             *
             * Com zoom aplicado, a primeira coisa que o gesto faz e desfazer o
             * zoom: entrar em tela cheia com a imagem ampliada e arrastada
             * mostraria um pedaco do canto, nao a transmissao.
             */
            onDoubleClick={() => {
                if (hasDragged()) return
                if (isZoomed) {
                    resetZoom()
                } else {
                    void toggleFullscreen()
                }
            }}
        >
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={entry.isSelf || muted || deafened}
                style={{
                    transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})${mirrored ? ' scaleX(-1)' : ''}`,
                    transformOrigin: 'center center',
                    transition: isDragging ? 'none' : 'transform 120ms cubic-bezier(0.2, 0, 0, 1)',
                    willChange: isZoomed ? 'transform' : 'auto'
                }}
                className={cn('h-full w-full object-contain pointer-events-none select-none', mirrored && '-scale-x-100')}
            />

            {!entry.stream && !connectionFailed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-card/60">
                    <MriSpinner />
                    <p className="text-xs text-muted-foreground">Conectando com {entry.nickname}...</p>
                </div>
            )}

            {/*
              * O transporte desistiu desse peer. Icone sozinho nao resolve: sem
              * dizer o que fazer, a pessoa fica olhando um tile preto achando
              * que o app quebrou. O texto aponta pra causa mais comum (rede que
              * bloqueia conexao direta) e pro que da pra tentar.
              */}
            {connectionFailed && (
                <div
                    role="status"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/85 px-4 text-center"
                >
                    <WifiOff className="h-6 w-6 text-red-400" />
                    <p className="text-sm font-medium text-white">
                        Não foi possível conectar com {entry.nickname}
                    </p>
                    <p className="max-w-xs text-[11px] leading-relaxed text-muted-foreground">
                        O vídeo tentou várias vezes e não achou caminho até você. Costuma ser rede móvel,
                        VPN ou firewall bloqueando a conexão direta. Peça pra {entry.nickname} recarregar a
                        página, ou tente sair e entrar de novo — de preferência em outra rede.
                    </p>
                </div>
            )}

            {/*
              * Mesma tarja de falha, texto diferente: aqui o problema nao e
              * com outra pessoa, e a SUA publicacao que caiu no SFU. A sala
              * ve tela preta enquanto isso, entao a acao util e parar e
              * compartilhar de novo — nao recarregar a pagina de ninguem.
              */}
            {selfPublishFailed && (
                <div
                    role="status"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/85 px-4 text-center"
                >
                    <WifiOff className="h-6 w-6 text-red-400" />
                    <p className="text-sm font-medium text-white">
                        Sua transmissão caiu
                    </p>
                    <p className="max-w-xs text-[11px] leading-relaxed text-muted-foreground">
                        A conexão com o servidor de mídia caiu e ninguém na sala está recebendo essa
                        imagem. Pare de compartilhar e comece de novo.
                    </p>
                </div>
            )}

            {/*
              * Imagem congelada: trilha pausada pela rede (`mute`) ou conexao
              * em recuperacao. Sao a mesma coisa pra quem assiste, entao levam
              * a mesma tarja — o sufixo so diz que ja tem alguem tentando.
              */}
            {(trackMuted || recovering) && entry.stream && !connectionFailed && (
                <div className="pointer-events-none absolute left-2 top-2 flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-black/75 px-2 py-1 text-amber-400">
                    <WifiOff className="h-3.5 w-3.5 animate-pulse" />
                    <span className="text-[10px] font-medium">
                        {recovering ? 'Conexão fraca · reconectando' : 'Conexão fraca'}
                    </span>
                </div>
            )}

            {/* Minimap e barra de zoom no canto inferior direito (estilo Discord) */}
            {isZoomed && !compact && (
                <ZoomMinimap
                    stream={entry.stream}
                    mirrored={mirrored}
                    scale={scale}
                    viewportBox={viewportBox}
                    onZoomIn={zoomIn}
                    onZoomOut={zoomOut}
                    onSetScale={setScaleDirect}
                    onReset={resetZoom}
                    onPanToNormalized={panToNormalized}
                />
            )}

            {/* Etiqueta de quem esta transmitindo — sempre visivel, discreta. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/85 to-transparent px-3 py-2">
                <span className="relative flex shrink-0 items-center justify-center">
                    <span
                        className={cn(
                            'absolute -inset-0.5 rounded-full transition-all duration-150',
                            speaking ? 'opacity-100 ring-2 ring-emerald-500' : 'opacity-0 ring-0'
                        )}
                        aria-hidden
                    />
                    <Avatar nickname={entry.nickname} avatar={entry.avatar} size={20} />
                </span>
                {entry.kind === 'camera'
                    ? <Video className="h-3 w-3 shrink-0 text-white/60" aria-label="camera" />
                    : <MonitorPlay className="h-3 w-3 shrink-0 text-white/60" aria-label="tela" />}
                <span className={cn(
                    'truncate text-xs font-medium transition-colors',
                    speaking ? 'text-emerald-400' : 'text-white/90'
                )}>
                    {entry.nickname}
                </span>
                {entry.isHost && <Crown className="h-3 w-3 shrink-0 text-amber-400" aria-label="host" />}
                {entry.isSelf && <MriStatusBadge label="voce" variant="success" size="xs" />}

                {/* Cabe ate no `compact`: sao duas palavras, e e justamente na
                    miniatura que a transmissao fica esquecida no canto. */}
                {semSom && (
                    <span
                        className="flex shrink-0 items-center gap-1 rounded bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-200"
                        title={'Esta transmissao esta indo sem som. Pra mandar audio junto, '
                            + 'pare e compartilhe de novo marcando a opcao de audio no seletor '
                            + 'do navegador.'}
                    >
                        <VolumeX className="h-3 w-3" aria-hidden />
                        sem som
                    </span>
                )}

                {/* Fora do `compact`: a miniatura da tira tem 160 px e o nome
                    ja disputa o espaco com o icone. */}
                {entry.isSelf && entry.watchers !== undefined && !compact && (
                    <span
                        className="shrink-0 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white/80"
                        title={entry.watchers === 0
                            ? 'Enquanto ninguém assiste, nada é enviado e o encoder fica parado'
                            : 'Quantas pessoas estão assistindo'}
                    >
                        {entry.watchers === 0 ? 'Ninguém assistindo' : `${entry.watchers} assistindo`}
                    </span>
                )}

                {etiqueta && !compact && (
                    <span
                        className="shrink-0 rounded bg-black/65 px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums text-white/70"
                        title={entry.isSelf
                            ? 'Resolucao, quadros por segundo e banda que voce esta enviando'
                            : 'Resolucao, quadros por segundo e banda que estao chegando ate voce'}
                    >
                        {etiqueta}
                    </span>
                )}

                {/*
                 * Som da transmissao: SEMPRE visivel quando ha audio, e nao no
                 * grupo que aparece so no hover. Escondido, o botao de desligar
                 * some junto com o mouse — quem ligou o som fica sem como
                 * desligar sem sair procurando.
                 */}
                {hasAudio && !compact && (
                    <div
                        className="pointer-events-auto ml-auto flex shrink-0 items-center gap-1.5 rounded-lg bg-black/65 px-1.5 py-1"
                        onPointerDown={(event) => event.stopPropagation()}
                        onDoubleClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            title={muted ? 'Ouvir o áudio dessa transmissão' : 'Silenciar essa transmissão'}
                            aria-label={muted ? 'Ouvir o áudio dessa transmissão' : 'Silenciar essa transmissão'}
                            aria-pressed={!muted}
                            onClick={(event) => {
                                event.stopPropagation()
                                setMuted((value) => !value)
                            }}
                            className={cn(
                                'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
                                muted ? 'text-white/60 hover:text-white' : 'text-primary hover:text-primary/80'
                            )}
                        >
                            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </button>

                        {/* O volume so faz sentido com o som ligado. */}
                        {!muted && (
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.05}
                                value={volume}
                                title={`Volume: ${Math.round(volume * 100)}%`}
                                aria-label="Volume dessa transmissão"
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => onVolumeChange?.(entry.id, Number(event.target.value))}
                                className="h-1 w-16 cursor-pointer accent-primary"
                            />
                        )}
                    </div>
                )}
            </div>

            {!compact && (
                <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <TileAction
                        label="Aumentar zoom (scroll ou clique)"
                        onClick={zoomIn}
                    >
                        <ZoomIn className="h-4 w-4" />
                    </TileAction>
                    {isZoomed && (
                        <>
                            <TileAction
                                label="Diminuir zoom"
                                onClick={zoomOut}
                            >
                                <ZoomOut className="h-4 w-4" />
                            </TileAction>
                            <TileAction
                                label="Redefinir zoom (100%)"
                                onClick={resetZoom}
                            >
                                <RotateCcw className="h-4 w-4" />
                            </TileAction>
                        </>
                    )}
                    <TileAction
                        label={isFullscreen ? 'Sair da tela cheia' : 'Ver em tela cheia'}
                        onClick={() => void toggleFullscreen()}
                    >
                        {isFullscreen ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
                    </TileAction>
                    {onPip && (
                        <TileAction
                            label={isPip ? 'Trazer de volta da janela' : 'So esta, em janela flutuante'}
                            onClick={() => onPip(entry.id)}
                        >
                            <PictureInPicture2 className={cn('h-4 w-4', isPip && 'text-primary')} />
                        </TileAction>
                    )}
                    {/* So com quem saiba trocar o modo: na janela flutuante e
                        no mosaico de uma tela so, o botao ficaria sem efeito. */}
                    {onToggleFocus && (
                        <TileAction
                            label={focused ? 'Voltar pro mosaico' : 'Destacar essa tela'}
                            onClick={() => onToggleFocus(entry.id)}
                        >
                            {focused ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </TileAction>
                    )}
                    {onHide && (
                        <TileAction
                            label={entry.isSelf ? 'Ocultar minha prévia' : 'Parar de assistir'}
                            onClick={() => onHide(entry.id)}
                        >
                            <X className="h-4 w-4" />
                        </TileAction>
                    )}
                </div>
            )}

            {compact && (
                <div className="pointer-events-none absolute left-2 top-2">
                    {entry.kind === 'camera'
                        ? <Video className="h-3.5 w-3.5 text-primary/80" />
                        : <MonitorPlay className="h-3.5 w-3.5 text-primary/80" />}
                </div>
            )}

            {menuEm && (
                <TileMenu
                    x={menuEm.x}
                    y={menuEm.y}
                    actions={acoesDoMenu}
                    onClose={() => setMenuEm(null)}
                />
            )}
        </div>
    )
}

/**
 * O palco re-renderiza inteiro a cada 100 ms — e o ritmo em que o medidor de
 * voz reavalia quem esta falando — e tambem sempre que qualquer tile reporta a
 * propria proporcao ou que a conexao de qualquer peer muda de estado. Sem esta
 * barreira, um mosaico de oito telas refazia oito tiles inteiros (cada um com
 * seu hook de zoom, seus efeitos e seus controles) por causa de um anel verde
 * que acendeu num deles.
 *
 * O `style` precisa de comparacao propria: o palco monta um objeto novo a cada
 * render pra dizer o tamanho do tile, e a comparacao rasa padrao veria sempre
 * uma prop diferente — a barreira nao pararia nada.
 */
export const StreamTile = memo(StreamTileBase, (anterior, proximo) => {
    const chaves = Object.keys({ ...anterior, ...proximo }) as (keyof StreamTileProps)[]
    for (const chave of chaves) {
        if (chave === 'style') continue
        if (anterior[chave] !== proximo[chave]) return false
    }
    return sameStyle(anterior.style, proximo.style)
})

function sameStyle(a?: React.CSSProperties, b?: React.CSSProperties): boolean {
    if (a === b) return true
    if (!a || !b) return false
    const chaves = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof React.CSSProperties>
    for (const chave of chaves) {
        if (a[chave] !== b[chave]) return false
    }
    return true
}

function TileAction({
    label,
    onClick,
    children
}: {
    label: string
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            onClick={(event) => {
                event.stopPropagation()
                onClick()
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-black/75 text-white/80 transition-colors hover:bg-black/80 hover:text-white"
        >
            {children}
        </button>
    )
}

